// src/components/operationCenter/widgets/DailyRecapWidget.tsx
// 24-hour AI analysis recap of news and tweets

import React from "react";
import {
  Brain,
  Newspaper,
  Twitter,
  AlertTriangle,
  Sparkles,
} from "lucide-react";
import type { WidgetProps } from "@/types/dashboard";

export interface DailyRecapConfig {
  // Future: filter by tokens, categories, etc.
  tokens?: string[];
}

// Placeholder data structure - replace with actual hook data later
interface RecapData {
  summary: string;
  sentiment: "bullish" | "bearish" | "neutral";
  sentimentScore: number; // -100 to 100
  keyTopics: string[];
  newsHighlights: Array<{
    title: string;
    sentiment: "positive" | "negative" | "neutral";
  }>;
  tweetHighlights: Array<{
    summary: string;
    sentiment: "positive" | "negative" | "neutral";
  }>;
  alerts: string[];
  generatedAt: string;
}

// Placeholder data - will be replaced by hook
const PLACEHOLDER_DATA: RecapData = {
  summary:
    "Markets showed mixed signals over the past 24 hours. Bitcoin consolidated around key support levels while altcoins displayed varied performance. Institutional interest remains strong with continued ETF inflows.",
  sentiment: "neutral",
  sentimentScore: 15,
  keyTopics: [
    "ETF Flows",
    "Fed Policy",
    "Layer 2 Scaling",
    "DeFi TVL",
    "Regulatory News",
  ],
  newsHighlights: [
    { title: "Bitcoin ETFs see $500M in net inflows", sentiment: "positive" },
    {
      title: "SEC delays decision on Ethereum ETF options",
      sentiment: "negative",
    },
    {
      title: "Major protocol announces layer 2 migration",
      sentiment: "positive",
    },
  ],
  tweetHighlights: [
    { summary: "Whale accumulation detected on-chain", sentiment: "positive" },
    {
      summary: "Concerns about macro headwinds persist",
      sentiment: "negative",
    },
    {
      summary: "New partnership announcements trending",
      sentiment: "positive",
    },
  ],
  alerts: [
    "Unusual volume detected in SOL/USDT",
    "BTC approaching key resistance at $105K",
  ],
  generatedAt: new Date().toISOString(),
};

// const SentimentBadge: React.FC<{
//   sentiment: "bullish" | "bearish" | "neutral";
//   score: number;
// }> = ({ sentiment, score }) => {
//   const config = {
//     bullish: {
//       bg: "bg-emerald-500/20",
//       text: "text-emerald-400",
//       icon: TrendingUp,
//     },
//     bearish: {
//       bg: "bg-rose-500/20",
//       text: "text-rose-400",
//       icon: TrendingDown,
//     },
//     neutral: {
//       bg: "bg-slate-500/20",
//       text: "text-slate-400",
//       icon: TrendingUp,
//     },
//   }[sentiment];
//   const Icon = config.icon;

//   return (
//     <div
//       className={`flex items-center gap-1.5 px-2 py-1 rounded-lg ${config.bg}`}
//     >
//       <Icon className={`w-3.5 h-3.5 ${config.text}`} />
//       <span className={`text-xs font-medium ${config.text} capitalize`}>
//         {sentiment}
//       </span>
//       <span className={`text-[10px] ${config.text} opacity-70`}>
//         ({score > 0 ? "+" : ""}
//         {score})
//       </span>
//     </div>
//   );
// };

const HighlightItem: React.FC<{
  text: string;
  sentiment: "positive" | "negative" | "neutral";
  icon: React.ReactNode;
}> = ({ text, sentiment, icon }) => {
  const dotColor = {
    positive: "bg-emerald-400",
    negative: "bg-rose-400",
    neutral: "bg-slate-400",
  }[sentiment];

  return (
    <div className="flex items-start gap-2 py-1.5">
      <div className="flex-shrink-0 mt-0.5 text-slate-500">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-300 leading-relaxed">{text}</p>
      </div>
      <div className={`w-2 h-2 rounded-full ${dotColor} flex-shrink-0 mt-1`} />
    </div>
  );
};

const DailyRecapWidget: React.FC<WidgetProps<DailyRecapConfig>> = ({
  widget,
}) => {
  // TODO: Replace with actual hook
  // const { data, isLoading, error } = useDailyRecap(widget.config);
  const data = PLACEHOLDER_DATA;
  const isLoading = false;

  if (isLoading) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-700/50 flex-shrink-0 bg-slate-800/30">
          <Brain className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
          <span className="text-xs uppercase tracking-wide text-slate-300">
            {widget.title}
          </span>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="flex items-center gap-2 text-slate-400">
            <Sparkles className="w-4 h-4 animate-pulse" />
            <span className="text-sm">Generating recap...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-700/50 flex-shrink-0 bg-slate-800/30">
        <Brain className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
        <span className="text-xs uppercase tracking-wide text-slate-300">
          {widget.title}
        </span>
        <div className="ml-auto flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-violet-500/15 border border-violet-500/20">
          <Sparkles className="w-3 h-3 text-violet-400" />
          <span className="text-[10px] text-violet-400/70">
            Last generated:
          </span>
          <span className="text-[10px] font-medium text-violet-300">
            {new Date(data.generatedAt).toLocaleString([], {
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {/* Summary */}
        <div>
          <p className="text-sm text-slate-200 leading-relaxed">
            {data.summary}
          </p>
        </div>

        {/* Key Topics */}
        <div>
          <h4 className="text-[10px] uppercase tracking-wide text-slate-500 mb-2">
            Key Topics
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {data.keyTopics.map((topic) => (
              <span
                key={topic}
                className="px-2 py-0.5 text-[10px] font-medium bg-slate-700/50 text-slate-300 rounded-full"
              >
                {topic}
              </span>
            ))}
          </div>
        </div>

        {/* News Highlights */}
        <div>
          <h4 className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1.5">
            <Newspaper className="w-3 h-3" />
            News Highlights
          </h4>
          <div className="space-y-0.5">
            {data.newsHighlights.map((item, idx) => (
              <HighlightItem
                key={idx}
                text={item.title}
                sentiment={item.sentiment}
                icon={<Newspaper className="w-3 h-3" />}
              />
            ))}
          </div>
        </div>

        {/* Tweet Highlights */}
        <div>
          <h4 className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1.5">
            <Twitter className="w-3 h-3" />
            Social Highlights
          </h4>
          <div className="space-y-0.5">
            {data.tweetHighlights.map((item, idx) => (
              <HighlightItem
                key={idx}
                text={item.summary}
                sentiment={item.sentiment}
                icon={<Twitter className="w-3 h-3" />}
              />
            ))}
          </div>
        </div>

        {/* Alerts */}
        {data.alerts.length > 0 && (
          <div>
            <h4 className="text-[10px] uppercase tracking-wide text-amber-500/80 mb-1.5 flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" />
              Alerts
            </h4>
            <div className="space-y-1">
              {data.alerts.map((alert, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 px-2 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-lg"
                >
                  <AlertTriangle className="w-3 h-3 text-amber-400 flex-shrink-0" />
                  <span className="text-xs text-amber-200">{alert}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-3 py-2 border-t border-slate-700/50 bg-slate-800/30 flex-shrink-0">
        <p className="text-[10px] text-slate-500">
          Generated {new Date(data.generatedAt).toLocaleTimeString()} •
          AI-powered analysis
        </p>
      </div>
    </div>
  );
};

export default DailyRecapWidget;
